import { Component, computed, inject, input, resource } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { KENDO_DROPDOWNBUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { KENDO_CARD } from '@progress/kendo-angular-layout';
import { AbstractGridComponent } from '@unity/abstract/abstract-grid.component';
import { AgreementsClient, ClientExpenseDetailedDto, ClientExpensesClient, FormTemplateClient } from '@unity/clients';
import { TypedDirective } from '@unity/directives/typed.directive';
import { DateTimezonePipe } from '@unity/pipes/date-timezone.pipe';
import { FormSchemaService } from '@unity/services/form-schema.service';
import { GridSharedComponent } from '@unity/shared/composed/grid-shared/grid-shared.component';
import { ChipWorkflowStateSharedComponent } from '@unity/shared/presentation/chip-workflow-state-shared/chip-workflow-state-shared.component';
import { SortConfig } from '@unity/utils/types';
import { GridcellCurrencySharedComponent } from '../presentation/gridcell-currency-shared/gridcell-currency-shared.component';
import { LinkGridcellSharedComponent } from '../presentation/link-gridcell-shared/link-gridcell-shared.component';

@Component({
  selector: 'unity-grid-client-expense-shared',
  standalone: true,
  imports: [
    KENDO_GRID,
    KENDO_CARD,
    KENDO_DROPDOWNBUTTON,
    GridSharedComponent,
    ChipWorkflowStateSharedComponent,
    TypedDirective,
    LinkGridcellSharedComponent,
    GridcellCurrencySharedComponent,
    GridcellCurrencySharedComponent,
    DateTimezonePipe,
  ],
  templateUrl: './grid-client-expense-shared.component.html',
  styleUrl: './grid-client-expense-shared.component.scss',
})
export class GridClientExpenseSharedComponent extends AbstractGridComponent<ClientExpenseDetailedDto> {
  private readonly clientExpensesClient = inject(ClientExpensesClient);
  private readonly agreementsClient = inject(AgreementsClient);
  private readonly formSchemasClient = inject(FormTemplateClient);
  private readonly formSchemaService = inject(FormSchemaService);

  /* Inputs */
  public readonly $agreementId = input.required<number>({ alias: 'agreementId' });
  public readonly $clientId = input<number | undefined>(undefined, { alias: 'clientId' });
  public readonly $gridTitle = input<string>('', { alias: 'gridTitle' });

  /* Computed */
  protected readonly $isClientView = computed(() => !this.$clientId());

  /* Resources */
  protected override resource = rxResource({
    params: () => {
      const query = this.$gridifyQuery();
      const agreementId = this.$agreementId();
      const clientId = this.$clientId();

      if (clientId && query) {
        query.filter = `client.id=${clientId}`;
      }
      return query && agreementId ? { query, agreementId } : undefined;
    },
    stream: ({ params: { query, agreementId } }) =>
      this.clientExpensesClient.getAllForAgreementAsDetailed(agreementId, query.page, query.pageSize, query.orderBy, query.filter),
  });

  protected readonly agreementResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      return agreementId ? { agreementId } : undefined;
    },
    stream: ({ params: { agreementId } }) => this.agreementsClient.getOneAsDetailed(agreementId),
  });

  protected readonly formSchemaResource = rxResource({
    params: () => {
      const agreement = this.agreementResource.value();
      const formSchemaId = agreement?.client_expense_form_schema?.id;
      return formSchemaId ? { formSchemaId } : undefined;
    },
    stream: ({ params: { formSchemaId } }) => this.formSchemasClient.getOneAsDetailed(formSchemaId),
  });

  private readonly surveyModelResource = resource({
    params: () => {
      const formSchema = this.formSchemaResource.value();
      const formSchemaData = formSchema?.data;
      return formSchemaData ? { formSchemaData } : undefined;
    },
    loader: ({ params: { formSchemaData } }) => this.formSchemaService.surveyModel(formSchemaData),
  });

  /* Data */
  protected defaultSort: SortConfig[] = [
    {
      field: 'created_dt',
      dir: 'desc',
    },
  ];

  protected getDisplayValue(value: string, questionName: string) {
    const surveyModel = this.surveyModelResource.value();
    if (!surveyModel) {
      return '';
    }

    const question = surveyModel.getQuestionByName(questionName);
    return question.getDisplayValue(false, value);
  }
}
